import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ILocalAGGrid from '../grid/iLocalAGGrid';
import { getDefaultColDef } from '../grid/defaultColDef';
import DashboardListGrid from './DashboardListGrid';

const REPORT_GRID_HEIGHT = 480;

/**
 * Reusable dashboard report dialog backed by AG Grid (clientSide).
 * Optional Pharmacy / Optical tabs for box utilization & stock age reports.
 * Uses fixed height + normal layout so page size / pagination work in the dialog.
 */
function DashboardReportDialog({
  open,
  title,
  onClose,
  loading,
  columnDefs,
  rowData,
  tabs,
  activeTab,
  onTabChange,
  getRowId,
  extraActions,
  paginationPageSize = 10,
  fetchPage,
}) {
  const showTabs = Array.isArray(tabs) && tabs.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {showTabs ? (
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{ mb: 1, minHeight: 40 }}
          >
            {tabs.map((t) => (
              <Tab key={t.value} label={t.label} value={t.value} />
            ))}
          </Tabs>
        ) : null}
        <Box sx={{ position: 'relative', height: REPORT_GRID_HEIGHT }}>
          {loading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.55)',
                zIndex: 2,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : null}
          {open && fetchPage ? (
            <DashboardListGrid
              key={`${title}-${activeTab}`}
              columnDefs={columnDefs}
              filterBody={{}}
              fetchPage={fetchPage}
              cacheBlockSize={paginationPageSize}
              paginationPageSize={paginationPageSize}
              height={REPORT_GRID_HEIGHT}
              rowHeight={42}
              getRowId={getRowId}
            />
          ) : open ? (
            <ILocalAGGrid
              key={`${title}-${activeTab != null ? activeTab : 'main'}`}
              columnDefs={columnDefs}
              defaultColDef={getDefaultColDef({
                minWidth: 100,
                flex: 1,
              })}
              rowModelType="clientSide"
              rowData={rowData || []}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={[10, 20, 25]}
              rowHeight={42}
              gridHeight={REPORT_GRID_HEIGHT}
              domLayout="normal"
              overlayNoRowsTemplate="No data found"
              getRowId={getRowId}
            />
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        {extraActions}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

DashboardReportDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
  columnDefs: PropTypes.array,
  rowData: PropTypes.array,
  tabs: PropTypes.array,
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTabChange: PropTypes.func,
  getRowId: PropTypes.func,
  extraActions: PropTypes.node,
  paginationPageSize: PropTypes.number,
  fetchPage: PropTypes.func,
};

export default DashboardReportDialog;



// WEBPACK FOOTER //
// ./src/components/dashboard2/DashboardReportDialog.js